import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, Directive, ElementRef, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChartComponent } from 'ng-apexcharts';
import { filter, fromEvent, merge, throttleTime } from 'rxjs';
import { clearApexChartInteraction } from 'shared/types/chart.config';

@Directive({
  selector: 'apx-chart[appHideApexChartTooltipOnScroll]',
  standalone: true
})
export class HideApexChartTooltipOnScrollDirective {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly chartComponent = inject(ChartComponent, { self: true });
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const eventOptions: AddEventListenerOptions = { capture: true, passive: true };
    const window = this.document.defaultView;
    const scrollEvents = [
      fromEvent(this.document, 'scroll', eventOptions),
      fromEvent(this.document, 'wheel', eventOptions),
      fromEvent(this.document, 'touchmove', eventOptions)
    ];

    if (window) {
      scrollEvents.push(fromEvent(window, 'scroll', eventOptions));
      scrollEvents.push(
        ...this.getScrollableAncestors(window).map(scrollable => fromEvent(scrollable, 'scroll', eventOptions))
      );
    }

    this.zone.runOutsideAngular(() => {
      merge(...scrollEvents)
        .pipe(throttleTime(16), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.clearChartInteraction());

      fromEvent<MouseEvent>(this.document, 'click', eventOptions)
        .pipe(
          filter(event => !this.elementRef.nativeElement.contains(event.target as Node | null)),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => this.clearChartInteraction());
    });
  }

  private clearChartInteraction(): void {
    clearApexChartInteraction(this.chartComponent.chartInstance());
  }

  private getScrollableAncestors(window: Window): HTMLElement[] {
    const scrollableAncestors: HTMLElement[] = [];
    let ancestor = this.elementRef.nativeElement.parentElement;

    while (ancestor) {
      if (this.isScrollableElement(ancestor, window)) {
        scrollableAncestors.push(ancestor);
      }

      ancestor = ancestor.parentElement;
    }

    return scrollableAncestors;
  }

  private isScrollableElement(element: HTMLElement, window: Window): boolean {
    const style = window.getComputedStyle(element);
    const overflowValues = [style.overflow, style.overflowX, style.overflowY];
    const canScroll = element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;

    return canScroll && overflowValues.some(value => value === 'auto' || value === 'scroll' || value === 'overlay');
  }
}
